import { createSlice } from '@reduxjs/toolkit';
import { camelCaseObjectKeys } from 'utils/camelCase';
import LoadingStatus from 'enums/loadingStatus';
import userProfileInitialState from './initialState';

const userProfileSlice = createSlice({
  name: 'userProfile',
  initialState: userProfileInitialState,
  reducers: {
    apply: (state, action) => 
      // return the new state with camel case object keys and isLoading set to false
      {

        const profile = camelCaseObjectKeys(action.payload);

        state.email = profile.email;
        state.id = profile.id;
        state.imageProfile = profile.imageProfile;
        state.isLogin = profile.isLogin;
        state.links = profile.links;
        state.livesOutside = profile.livesOutside;
        state.name = profile.name?.trim().replace(/\s+/g, ' ');
        state.phone = profile.phone;
        state.preferredContactMethod = profile.preferredContactMethod;
        state.hasUserOptedIntoRecommendationEmail = profile.hasUserOptedIntoRecommendationEmail;
        state.realtorQuestion = profile.realtorQuestion;
        state.userId = profile.userId;
        state.userLocation = profile.userLocation;
        state.zip = profile.zip;
        state.isLoading = false;
      },
    applyFavorites: (state, action) => {
      const payload = camelCaseObjectKeys(action.payload);
      state.favorites = payload?.favorites;
      state.isProfileFavoritesLoaded = true;
    },
    update: (state, action) => {
      const userInfo = {...state, ...action.payload};
      state.name = userInfo.name?.trim().replace(/\s+/g, ' ');
      state.email = userInfo.email;
      state.phone = userInfo.phone;
      state.zip = userInfo.zip;
      state.livesOutside = userInfo.livesOutside;
      state.preferredContactMethod = userInfo.preferredContactMethod || state.preferredContactMethod;
      state.hasUserOptedIntoRecommendationEmail = userInfo.hasUserOptedIntoRecommendationEmail ??
        state.hasUserOptedIntoRecommendationEmail;
      state.realtorQuestion = userInfo.realtorQuestion || state.realtorQuestion;
    },
    fetch: (state) => {
      state.isLoading = true;
    },
    handleError: (state) => {
      state.isLoading = false;
    },
    saveFavorite: (state, action) => {
      // check if the action.payload.id is already in the pendingSaves array, if not, add it.
      const pendingSaveExists = state.pendingSaves.some((favorite) => 
        Number(favorite.listingInformation.id) === Number(action.payload.listingInformation.id)
      );

      if (!pendingSaveExists) {

        // add the favorite to the pending array, in case the API call fails or the user is logged out
        // and we need to retry the API call once the user logs in.
        state.pendingSaves.push(action.payload);
      }

    },
    deleteFavorite: () => {},
    deleteFavoriteFullfilled: (state, action) => {
      const payload = action.payload;
      state.favorites = state.favorites.filter((favorite) => favorite.listingId !== payload.listingId);
    },
    saveFavoriteFulfilled: (state, action) => {
      const newFavorite = action.payload.data.favorite;

      // remove the favorite from the pending array
      state.pendingSaves = state.pendingSaves.filter(
        (favorite) => Number(favorite.listingInformation.id) !== Number(newFavorite.listingId)
      );
      
      const favoriteExists = state.favorites.find((favorite) => favorite.listingId === newFavorite.listingId);
      
      if (!favoriteExists) {
        // add the favorite to the favorites array
        state.favorites = [...state.favorites, newFavorite];
      }
    },
    updateNeustarInformation: (state, action) => {
      const neustarInformation = action.payload;
      state.neustarInformation = neustarInformation;
      state.neustarStatus = LoadingStatus.Succeeded.toString()
    },
}});

const authenticationSlice = createSlice({
  name: 'authentication',
  initialState: {
    isAuthLibraryLoaded: false,
    isAuthModalOpen: false,
    googleOneTapLibraryLoadingStatus: null,
  },
  reducers: {
    authLibraryLoaded: (state) => {
      state.isAuthLibraryLoaded = true;
    },
    authLibraryErrored: (state) => {
      state.isAuthLibraryLoaded = false;
    },
    googleOneTapLibraryLoadingStatus: (state, action) => {
      state.googleOneTapLibraryLoadingStatus = action.payload.name;
    },
  },
});

const modalSlice = createSlice({
  name: 'modal',
  initialState: {
    isAuthModalOpen: false
  },
  reducers: {
    onOpen: (state) => {
      state.isAuthModalOpen = true;
    },
    onClose: (state) => {
      state.isAuthModalOpen = false;
    }
  },
});

// Extract the action creators object and the reducer
export { userProfileSlice, authenticationSlice, modalSlice };
